/**
 * Générateur — Automatisme : prendre 1 %, 10 % ou 50 % d'un nombre, en lien
 * avec la proportionnalité.
 * Attendu ID : 1056 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 569).
 * Cadrage rédigé par le sous-agent programme-maths (sources Eduscol et DEPP,
 * épreuve de Musk incluse), arbitré par le prof à l'épreuve de Musk le
 * 2026-09-09.
 *
 * Le geste, un par cas : 50 %, la moitié par la numération décimale (70 →
 * 35), pas par les tables ; 10 %, le décalage d'un rang (35 → 3,5) ; 1 %, le
 * décalage de deux rangs (300 → 3). Le premier geste est de reconnaître le
 * cas. Jamais a/100 × N (4e), jamais 25 %, 5 %, 20 % (4e), jamais « quel
 * pourcentage ? » ni le tout à retrouver (objectifs 982, 4e). Le lien avec
 * la proportionnalité vit dans les grandeurs de la carte 2 (une fois sur
 * deux : 240 €, 350 g, 1 200 km) et dans la barre « 100 % » de la fiche.
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash             (4 lignes « 50 % de 70 = ⬚ », saisie)
 *   - Prendre un pourcentage  (QCM « Combien vaut 10 % de 240 € ? » 4 nombres,
 *                              Vrai/Faux 3 affirmations, un pourcentage
 *                              par ligne)
 * Supprimés à l'épreuve de Musk (cadrage puis prof) : carte « Quel calcul ? »
 * (pur rappel), Relier pourcentage ↔ fraction (automatisme suivant), QCM
 * « pourquoi », compositions 30 % et 5 % (4e), décimaux en entrée (et la
 * famille nombres_accoles avec), la famille soustraire (non attestée, reste
 * couvre « enlever »).
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Valeurs manipulées en CENTIÈMES entiers (exactitude). 50 % : N multiple de
 * 10 jusqu'à 1 000 ou de 100 jusqu'à 2 000 (« 50 % de 968 » serait un calcul
 * posé, revue), cas discriminant la dizaine impaire une fois sur deux (70,
 * 130, 350 : les tables ne suffisent pas). 10 % : N de 10 à 2 000, un tiers de non-multiples de 10
 * (35 → 3,5). 1 % : multiples de 100 (résultat entier) et de 10 (250 → 2,5)
 * ; les entiers de 20 à 99 (45 → 0,45, deux décimales) seulement en carte 2
 * (la saisie s'arrête à une décimale). Nombre nu en Série flash, grandeur
 * une fois sur deux en carte 2. SACRIFIÉ : 300, pour les trois pourcentages
 * (la fiche : 150, 30, 3), jamais tiré, auto-testé.
 *
 * ── Distracteurs (familles nommées, les mêmes partout) ───────────────────
 *   pourcentage_recopie : la réponse est a lui-même (10 % de 110 → 10) —
 *                         L'ERREUR PRINCIPALE, 40 % des élèves de 4e (DEPP
 *                         4e 2023 q.16) ;
 *   mauvais_diviseur    : le bon geste au mauvais rang (1 % de 250 → 25 ;
 *                         10 % de 240 → 2,4 ; 50 % de 12 → 3, ÷ 4) ;
 *   moitie_voisine      : 50 % seulement, la moitié d'un voisin rond (50 %
 *                         de 70 → 30, 40, 45 ; DEPP 4e 2023 q.2) ;
 *   sens_inverse        : multiplier au lieu de diviser (50 % de 70 → 140),
 *                         secondaire, jamais seule fausse d'un tableau ;
 *   reste               : ce qui reste (10 % de 240 → 216), jamais pour
 *                         50 % (égale la clé).
 * Chaque distracteur : > 0, deux décimales au plus, différent de la clé et
 * de N. Aucune famille imposée sur une fausse (leçon 1054).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1056;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_PCT = 'Prendre un pourcentage';

// ─── Le domaine ───────────────────────────────────────────────────────────

const PCTS = [50, 10, 1];
const N_MAX = 2000;
const UNITES = ['€', 'g', 'km', 'm', 'L'];

/** Le nombre de la fiche, sacrifié pour les trois pourcentages : 300. */
export const SACRIFIE = 300;

/** Centièmes → écriture française (350 → « 3,5 », 45 → « 0,45 », 4500 → « 45 »). */
const fmt = (c) => {
    const e = Math.floor(c / 100);
    const r = c % 100;
    return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`;
};
/** « a % de N » en texte (espace insécable avant %). */
const pctDe = (it) => `${it.pct} % de ${it.N}${it.unite ? ` ${it.unite}` : ''}`;
const avecUnite = (c, it) => `${fmt(c)}${it.unite ? ` ${it.unite}` : ''}`;

/** Un item : { pct, N, val (centièmes), unite } ; null hors domaine. */
const item = (pct, N, unite = null) => {
    if (N === SACRIFIE || N < 10 || N > N_MAX) return null;
    const val = N * pct; // a % de N = N × a / 100, en centièmes : N × a
    return { pct, N, val, unite };
};

/** Cas discriminant : dizaine impaire pour 50 % ; non-multiple de 10 pour 10 % ; non-multiple de 100 pour 1 %. */
const estDiscriminant = (it) => (it.pct === 50 ? it.N % 20 === 10 : (it.pct === 10 ? it.N % 10 !== 0 : it.N % 100 !== 0));

/**
 * Un item d'un pourcentage donné. `carte2` ouvre les entiers 20 à 99 pour
 * 1 % (deux décimales) ; `discriminant` impose ou interdit le cas.
 */
const tirerItem = (pct, { carte2 = false, discriminant = null, unite = null, exclusN = [] } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        let N;
        // 50 % : multiples de 10 jusqu'à 1 000 et de 100 jusqu'à 2 000 (revue
        // 2026-09-09 : « 50 % de 968 » est un calcul posé, pas un regard) ;
        // le cas discriminant, la dizaine impaire (70 → 35), une fois sur deux
        if (pct === 50) N = randomChoice([true, false]) ? 10 * (2 * randInt(0, 49) + 1) : randomChoice([true, false]) ? 10 * randInt(1, 100) : 100 * randInt(10, 20);
        else if (pct === 10) N = randomChoice([true, false, false]) ? randInt(10, N_MAX) : 10 * randInt(1, N_MAX / 10);
        else {
            const forme = randomChoice(carte2 ? ['cent', 'dix', 'unite'] : ['cent', 'dix']);
            N = forme === 'cent' ? 100 * randInt(1, N_MAX / 100) : (forme === 'dix' ? 10 * randInt(1, N_MAX / 10) : randInt(20, 99));
        }
        if (exclusN.includes(N)) continue;
        const it = item(pct, N, unite);
        if (!it) continue;
        if (discriminant === true && !estDiscriminant(it)) continue;
        if (discriminant === false && estDiscriminant(it)) continue;
        return it;
    }
    return null;
};

/** Replis déterministes vérifiés : 50 % de 70, 10 % de 35, 1 % de 400, 10 % de 240. */
const REPLIS = [item(50, 70), item(10, 35), item(1, 400), item(10, 240)];

const EXPLICATIONS = {
    pourcentage_recopie: '10 %, ce n\'est pas 10 : c\'est 10 pour 100. Je partage le nombre en 100 et j\'en prends 10, soit une part sur 10.',
    mauvais_diviseur: '50 %, c\'est la moitié (2 parts) ; 10 %, une part sur 10 ; 1 %, une part sur 100. Je vérifie en combien de parts je partage.',
    moitie_voisine: 'La moitié de 70 : la moitié de 60, c\'est 30, et la moitié de 10, c\'est 5. Donc 35.',
    sens_inverse: 'Prendre 50 %, 10 % ou 1 %, c\'est prendre une partie : le résultat est plus petit que le nombre.',
    reste: 'On demande la partie prise, pas ce qui reste : 10 % de 240, c\'est 24.',
    regle: '10 %, c\'est 10 pour 100 : je partage le nombre en 10 parts égales et j\'en prends une. 50 %, c\'est la moitié. 1 %, une part sur 100.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

const DIVISEURS_FAUX = { 50: [4, 10], 10: [2, 100], 1: [10, 1000] };

/** Tous les candidats d'un item (valeurs en centièmes entiers, > 0, ≠ clé, ≠ N). */
const tousCandidats = (it) => {
    const c = [];
    const pousser = (code, val) => { if (Number.isInteger(val) && val > 0 && val !== it.val && val !== it.N * 100) c.push({ code, val }); };
    pousser('pourcentage_recopie', it.pct * 100);
    for (const m of DIVISEURS_FAUX[it.pct]) if ((it.N * 100) % m === 0) pousser('mauvais_diviseur', (it.N * 100) / m);
    if (it.pct === 50) for (const dv of [-20, -10, 10, 20]) pousser('moitie_voisine', ((it.N + dv) * 100) / 2);
    pousser('sens_inverse', it.N * 100 * (100 / it.pct));
    if (it.pct !== 50) pousser('reste', it.N * 100 - it.val);
    return c;
};

/** Une variante au hasard par famille (Vrai/Faux). */
const candidats = (it) => {
    const parCode = new Map();
    for (const x of shuffle(tousCandidats(it))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};

/**
 * QCM : trois familles distinctes, tirées par RANG de la clé parmi les quatre
 * nombres (presque toutes les erreurs dépassent la clé : recopie, reste, sens
 * inverse, ÷ 10 pour 1 % ; seuls ÷ 4, ÷ 100, ÷ 1000 et la moitié voisine − 10
 * passent dessous). Poids vers les rangs rares ; mesuré à la revue.
 */
const distracteursQcm = (it) => {
    const cands = tousCandidats(it);
    const parRang = new Map();
    for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
            for (let k = j + 1; k < cands.length; k++) {
                const t = [cands[i], cands[j], cands[k]];
                if (new Set(t.map((x) => x.code)).size < 3 || new Set(t.map((x) => x.val)).size < 3) continue;
                const rang = t.filter((x) => x.val < it.val).length;
                if (!parRang.has(rang)) parRang.set(rang, []);
                parRang.get(rang).push(t);
            }
        }
    }
    if (!parRang.size) return null;
    // Pour 50 % les candidats plus petits abondent (÷ 4, ÷ 10, moitiés voisines
    // − 10 et − 20), pour 10 % et 1 % un seul (÷ 100, ÷ 1000) : tirage uniforme
    // parmi les rangs possibles, le poids 4 sur les rangs hauts donnait « la clé
    // est le 3e nombre » 44 % du temps. Mesuré à la revue.
    const POIDS = { 0: 1, 1: 1, 2: 1, 3: 1 };
    const urne = [...parRang.keys()].flatMap((r) => Array(POIDS[r]).fill(r));
    return randomChoice(parRang.get(randomChoice(urne)));
};

// ─── Carte 1 : Série flash (4 lignes, saisie) ─────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

const ligneFlash = (it) => `${pctDe(it)} = {{${fmt(it.val)}}}`;
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => paire.map(ligneFlash).join(' ; ')).join('\n');

const payloadFlash = (items) => {
    // strict false : « 3,5 » et « 3.5 » comptent juste, comme toute saisie décimale du parc
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, pourcentage_recopie: EXPLICATIONS.pourcentage_recopie } };
    payload._meta.params = { items };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        // les trois pourcentages, l'un doublé ; quatre nombres et quatre résultats distincts ; un cas discriminant
        const pcts = shuffle([...PCTS, randomChoice(PCTS)]);
        const items = [];
        for (const pct of pcts) {
            const it = tirerItem(pct, { exclusN: items.map((x) => x.N) });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 4) continue;
        if (new Set(items.map((it) => it.val)).size < 4) continue;
        if (!items.some(estDiscriminant)) continue;
        return payloadFlash(items);
    }
    return payloadFlash(REPLIS);
};

// ─── Carte 2, format A : QCM, quatre nombres ──────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const unite = randomChoice([null, randomChoice(UNITES)]);
        const it = tirerItem(randomChoice(PCTS), { carte2: true, unite });
        if (!it) continue;
        const faux = distracteursQcm(it);
        if (!faux) continue;
        const bon = avecUnite(it.val, it);
        const choix = [bon, ...faux.map((f) => avecUnite(f.val, it))];
        if (new Set(choix).size !== 4) continue;
        const texte = `<p><b>Combien vaut ${pctDe(it)} ?</b></p>`;
        const payload = makeQcm(choix, bon, texte, opts(L_PCT));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 10 % de 240 = 24 ; pourcentage_recopie 10, reste 216, mauvais_diviseur 2,4.
    const it = REPLIS[3];
    const payload = makeQcm(['24', '10', '216', '2,4'], '24', `<p><b>Combien vaut ${pctDe(it)} ?</b></p>`, opts(L_PCT));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'pourcentage_recopie' }, { code: 'reste' }, { code: 'mauvais_diviseur' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 affirmations, un pourcentage par ligne) ─

/** « a % de N, c'est v. », vraie (v = clé) ou fausse (v d'une famille). */
const affirmation = (it, vraie) => {
    if (vraie) return { ligne: `${pctDe(it)}, c'est ${avecUnite(it.val, it)}.`, code: null };
    const cands = candidats(it);
    if (!cands.length) return null;
    const d = randomChoice(cands);
    return { ligne: `${pctDe(it)}, c'est ${avecUnite(d.val, it)}.`, code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const unite = randomChoice([null, randomChoice(UNITES)]);
        const items = [];
        for (const pct of shuffle([...PCTS])) {
            const it = tirerItem(pct, { carte2: true, unite, exclusN: items.map((x) => x.N) });
            if (!it) break;
            items.push(it);
        }
        if (items.length < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => affirmation(it, verites[i]));
        if (affs.some((a) => !a)) continue;
        const codes = affs.filter((a) => a.code).map((a) => a.code);
        // sens_inverse : jamais seule fausse (la taille l'élimine sans le geste), une fois au plus
        const nbInv = codes.filter((c) => c === 'sens_inverse').length;
        if (nbInv > 1 || (nbInv === 1 && codes.length === 1)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_PCT));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 50 % de 70, c'est 35 (vrai) ; 10 % de 35, c'est 10 (faux, pourcentage_recopie) ; 1 % de 400, c'est 40 (faux, mauvais_diviseur ÷ 10).
    const items = REPLIS.slice(0, 3);
    const lignes = [
        `${pctDe(items[0])}, c'est 35.`,
        `${pctDe(items[1])}, c'est 10.`,
        `${pctDe(items[2])}, c'est 40.`,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_PCT));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, false], distracteurs: [{ code: 'pourcentage_recopie' }, { code: 'mauvais_diviseur' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois pourcentages (50, 10, 1) ; N entier de 10 à 2 000 (multiple de 10 ou de 100 pour 50 %, dizaine impaire une fois sur deux), résultat entier ou décimal ; 300 sacrifié pour les trois ; nombre nu en Série flash, grandeur une fois sur deux en carte 2',
        'chaque distracteur : > 0, deux décimales au plus, différent de la clé et de N ; aucune famille imposée sur une fausse',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    pct_qcm_unique: {
        support: '« Combien vaut a % de N ? », quatre nombres, trois familles distinctes tirées par rang de la clé ; résidu mesuré à la revue',
        familles: {
            pourcentage_recopie: 'a lui-même (10 % de 110 → 10), l\'erreur principale',
            mauvais_diviseur: 'N ÷ m au mauvais rang : 50 % → ÷ 4, ÷ 10 ; 10 % → ÷ 2, ÷ 100 ; 1 % → ÷ 10, ÷ 1000',
            moitie_voisine: '50 % seulement, (N ± 10 ou 20) ÷ 2',
            sens_inverse: 'N × 2, × 10, × 100 ; secondaire, jamais seule fausse d\'un tableau',
            reste: 'N − clé, jamais pour 50 %',
        },
    },
    pct_tableau_vf: 'trois affirmations, un pourcentage par ligne, 1 ou 2 vraies, familles libres, sens_inverse jamais seule ; résidu structurel mesuré à la revue (5 000 tableaux) : « la réponse répète le pourcentage → faux, sinon vrai » 63 %, pourcentage_recopie étant l\'erreur principale et visible à sa forme, assumé comme sur 1054',
    flash_texte_trou_saisie: 'quatre lignes, les trois pourcentages (l\'un doublé), quatre nombres et quatre résultats distincts, un cas discriminant, résultat à une décimale au plus',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    pct_qcm_unique: genQcm,
    pct_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'pct', label: L_PCT, formatsList: [
        { key: 'pct_qcm_unique', label: 'QCM' },
        { key: 'pct_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
